import React, { useState, useEffect } from "react";
import { actionsApi } from "../../services/api";
import type {
  EmailAction,
  SmsAction,
  RunQueryAction,
} from "../../types/node_action";
import {
  Mail,
  MessageSquare,
  Database,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface ActionsReferenceProps {
  className?: string;
}

export default function ActionsReference({
  className = "",
}: ActionsReferenceProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState<{
    email: EmailAction[];
    sms: SmsAction[];
    run_query: RunQueryAction[];
  }>({ email: [], sms: [], run_query: [] });

  useEffect(() => {
    if (isExpanded && !loading) {
      loadActions();
    }
  }, [isExpanded]);

  const loadActions = async () => {
    setLoading(true);
    try {
      const [emailActions, smsActions, runQueryActions] = await Promise.all([
        actionsApi.listEmailActions(),
        actionsApi.listSmsActions(),
        actionsApi.listRunQueryActions(),
      ]);

      setActions({
        email: emailActions,
        sms: smsActions,
        run_query: runQueryActions,
      });
    } catch (error) {
      console.error("Failed to load actions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "sms":
        return <MessageSquare className="h-4 w-4" />;
      case "run_query":
        return <Database className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getActionTypeLabel = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return "Email Actions";
      case "sms":
        return "SMS Actions";
      case "run_query":
        return "Run Query Actions";
      default:
        return type;
    }
  };

  return (
    <div className={`bg-gray-50 rounded-lg border ${className}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">
            Available Actions Reference
          </span>
          <span className="text-sm text-gray-500">
            (
            {actions.email.length +
              actions.sms.length +
              actions.run_query.length}{" "}
            total)
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {loading ? (
            <div className="text-center py-4 text-gray-500">
              Loading actions...
            </div>
          ) : (
            <>
              {(["email", "sms", "run_query"] as const).map((type) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    {getActionIcon(type)}
                    {getActionTypeLabel(type)}
                    <span className="text-xs text-gray-500">
                      ({actions[type].length})
                    </span>
                  </div>

                  {actions[type].length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {actions[type].map((action) => (
                        <div
                          key={action.id}
                          className="bg-white p-3 rounded border text-sm"
                        >
                          <div className="font-medium text-gray-900">
                            {action.name}
                          </div>
                          {action.description && (
                            <div className="text-gray-600 text-xs mt-1">
                              {action.description}
                            </div>
                          )}
                          <div className="text-gray-500 text-xs mt-1">
                            ID: {action.id}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      No {type.replace("_", " ")} actions available
                    </div>
                  )}
                </div>
              ))}

              <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
                <strong>Tip:</strong> Use these action names when configuring
                button actions. The action name must exactly match one of the
                names listed above.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
